import type { Computed, Action, Thunk, ThunkOn } from 'easy-peasy';
import { computed, action, thunk, thunkOn } from 'easy-peasy';
import type {
  AuthenticationResponse,
  AuthenticationRequest,
  User,
  ResetPasswordRequest,
  PartnerLink,
  VersionMetadata,
} from '../services/AuthService/types';
import type { ChangePasswordRequest } from '../services/PartnerService/types';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions, SetActivePartnerPayload } from '../../types';
import type { StoreModel } from '../';
import { push } from 'redux-first-history';
import parseStatusCode from '../errors/parseStatusCode';
import config from '../../config';
import Cookies from 'universal-cookie';
const { ACCESS_TOKEN_NAME, COOKIE_TOKEN_EXPIRES, LOGGED_IN_COOKIE, PARTNER_COOKIE_NAME, REFRESH_TOKEN_NAME } = config;

export const extractPartnerSlug = (path: string) => {
  return path.split('/')[1];
};

export const cookies = new Cookies();
export const getToken = () => cookies.get(ACCESS_TOKEN_NAME);
// export const getRefreshToken = () => cookies.get(REFRESH_TOKEN_NAME);
export const getPartnerId = () => cookies.get(PARTNER_COOKIE_NAME);
export const getLoginCookie = () => cookies.get(LOGGED_IN_COOKIE);
export const getTokenExpiry = () => cookies.get(COOKIE_TOKEN_EXPIRES);

export interface SessionModel {
  user?: User;
  lastAction?: number;
  accessToken?: string;
  refreshToken?: string;
  changePasswordErrorMessage?: string;
  passwordResetSubmitted?: boolean;
  setChangePasswordErrorMessage: Action<SessionModel, string | undefined>;
  versionMetadata?: VersionMetadata;
  startRefreshPolling: ThunkOn<SessionModel, Injections, StoreModel>;
  checkPartnerUpdate: ThunkOn<SessionModel, void, StoreModel>;
  checkSessionPartner: ThunkOn<SessionModel, void, StoreModel>;
  setActivePartner: Thunk<SessionModel, SetActivePartnerPayload, Injections, StoreModel>;
  switchActivePartner: Action<SessionModel, string>;
  navigateHome: Thunk<SessionModel, void, Injections, StoreModel>;
  setPasswordResetSubmittedFlag: Action<SessionModel, boolean>;
  setVersionMetadata: Action<SessionModel, VersionMetadata>;
  authenticateUser: Thunk<
    SessionModel,
    AuthenticationRequest,
    Injections,
    StoreModel,
    Promise<AuthenticationResponse | undefined>
  >;
  authenticationSuccess: Action<SessionModel, AuthenticationResponse>;
  authenticationSuccessSilent: Action<SessionModel, AuthenticationResponse>;
  authenticationFailure: Action<SessionModel, ApplicationErrorOptions>;
  changePassword: Thunk<SessionModel, ChangePasswordRequest, Injections, StoreModel, Promise<void>>;
  requestPasswordReset: Thunk<SessionModel, ResetPasswordRequest, Injections, StoreModel, Promise<void>>;
  fetchApplicationVersion: Thunk<SessionModel, void, Injections, StoreModel, Promise<VersionMetadata>>;
  sessionActionStart: Action<SessionModel, void>;
  logoutSso: Thunk<SessionModel, void, Injections, StoreModel>;
  verifyPartner: Action<SessionModel, void>;
  refreshSession: Thunk<SessionModel, void, Injections, StoreModel>;
  refreshSessionSilent: Thunk<SessionModel, void, Injections, StoreModel>;
  isLoggedIn: Computed<SessionModel, boolean>;
  canEdit: Computed<SessionModel, boolean>;
  isAdmin: Computed<SessionModel, boolean>;
  setSessionUser: Action<SessionModel, User>;
  isRefreshing?: boolean;
  setRefreshing: Action<SessionModel, boolean>;
}

let pollingTimer: NodeJS.Timeout | undefined;
let cookieTimer: NodeJS.Timeout | undefined;
const COOKIE_POLLING_INTERVAL = 10 * 1000; // 10 seconds
const POLLING_INTERVAL_MAX = 30 * 60 * 1000; // 30 minutes max interval
// const POLLING_INTERVAL_MAX = 1 * 60 * 1000; // debug 1 minutes max interval
const POLLING_RANDOM_OFFSET_SECONDS_MIN = 19;
const POLLING_RANDOM_OFFSET_SECONDS_MAX = 53;

export const sessionModel: SessionModel = {
  user: undefined,
  isRefreshing: false,
  sessionActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  setPasswordResetSubmittedFlag: action((state, payload) => {
    state.passwordResetSubmitted = payload;
  }),
  setActivePartner: thunk(async (actions, payload, { injections, dispatch, getStoreState }) => {
    try {
      clearInterval(pollingTimer);
      const res = await injections.authService.changePartnerContext(payload.partnerId);
      actions.switchActivePartner(payload.partnerId);
      if (res.isAuthenticated) {
        window.location.href = '/sso';
      } else {
        const path = window.location.toString().split('/');
        const pathParams = path.slice(4).join('/');
        const state = getStoreState();
        const returnSlug = `/${state?.session?.user?.sessionPartnerUrl}/${payload.discardPath ? '' : pathParams}`;
        dispatch(push(returnSlug));
        window.location.reload();
      }
    } catch (error) {
      actions.authenticationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Partner Switch Failed',
        asNotification: true,
      });
    }
  }),
  navigateHome: thunk(async (actions, payload, { dispatch, getStoreState }) => {
    const state = getStoreState();
    dispatch(push('/' + state.session.user?.sessionPartnerUrl));
  }),
  switchActivePartner: action((state, payload) => {
    if (!state.user) {
      return;
    }
    const partner = state.user.partners?.find((p: PartnerLink) => p.partnerId === payload);
    if (partner) {
      state.user.sessionPartnerId = partner.partnerId;
      state.user.sessionPartnerName = partner.partnerName;
      state.user.sessionPartnerRole = partner.role;
      state.user.sessionPartnerUrl = partner.urlSlugName;
    }
  }),
  authenticateUser: thunk(async (actions, payload, { injections }) => {
    try {
      actions.sessionActionStart();
      const { authService } = injections;
      const response = await authService.authenticateUser(payload);
      if (!response.resetToken) {
        actions.authenticationSuccess(response);
      }
      return response;
    } catch (error) {
      actions.authenticationFailure({
        error,
        message: 'Invalid credentials.',
        asNotification: true,
      });
      return undefined;
    }
  }),
  startRefreshPolling: thunkOn(
    (actions, storeActions) => [
      storeActions.session.authenticationSuccess,
      storeActions.session.authenticationSuccessSilent,
    ],
    (actions, payload) => {
      if (cookieTimer) {
        clearInterval(cookieTimer);
      }

      /**
       * Poll the refresh endpoint on an interval shorter than the
       * token lifetime. Subtract a random offset between [MIN, MAX]
       * to mitigate multiple tab races.
       *
       * Poll the expiresAt cookie for changes from other tabs and reset the interval.
       *
       * Service endpoint will only perform a refresh near expiry, otherwise user info is
       * requested with existing token and returned with updated cookies.
       */
      const startPollingInterval = function () {
        if (pollingTimer) {
          clearInterval(pollingTimer);
        }
        const now = new Date();
        const expiresIn = +(getTokenExpiry() || now.getTime() + POLLING_INTERVAL_MAX) - now.getTime();

        const randomSeconds =
          Math.random() * (POLLING_RANDOM_OFFSET_SECONDS_MAX - POLLING_RANDOM_OFFSET_SECONDS_MIN) +
          POLLING_RANDOM_OFFSET_SECONDS_MIN;
        const randomMilliseconds = Math.floor(randomSeconds * 1000);

        // clamp interval to [0, expiry] minutes
        const randomExpiresIn = expiresIn - randomMilliseconds;
        const randomInterval = Math.min(Math.max(randomExpiresIn, 0), POLLING_INTERVAL_MAX - randomMilliseconds);

        pollingTimer = setInterval(() => {
          actions.refreshSessionSilent();
        }, randomInterval /* [0, 28] mins */);
      };

      const checkExpiryCookie = (function () {
        let prev = cookies.get(COOKIE_TOKEN_EXPIRES);

        return function () {
          const current = cookies.get(COOKIE_TOKEN_EXPIRES);
          if (current !== prev) {
            prev = current;
            return startPollingInterval();
          }
        };
      })();

      startPollingInterval();
      cookieTimer = setInterval(checkExpiryCookie, COOKIE_POLLING_INTERVAL);
    },
  ),
  changePassword: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const storeActions = getStoreActions();
      actions.sessionActionStart();
      const { partnerService } = injections;
      actions.setChangePasswordErrorMessage(undefined);
      const response = await partnerService.changePassword(payload);

      storeActions.messages.dispatchMessage({
        noticeType: 'success',
        content: 'Password changed succesfully.',
      });
      return response;
    } catch (error) {
      if (/reused/.test(error.message)) {
        actions.setChangePasswordErrorMessage(error.message);
      } else {
        actions.authenticationFailure({
          error,
          message: error.message,
          asNotification: true,
        });
      }
      throw new Error(error);
    }
  }),
  requestPasswordReset: thunk(async (actions, payload, { injections, getStoreActions, getStoreState }) => {
    const storeActions = getStoreActions();
    const storeState = getStoreState();
    try {
      actions.sessionActionStart();
      const { authService } = injections;
      const response = await authService.requestPasswordReset(payload);
      if (storeState.session.user) {
        storeActions.messages.dispatchMessage({
          noticeType: 'success',
          content: `A one-time link to change your password has been sent.`,
        });
      } else {
        actions.setPasswordResetSubmittedFlag(true);
      }
      return response;
    } catch (error) {
      actions.setPasswordResetSubmittedFlag(false);
      storeActions.messages.dispatchMessage({
        noticeType: 'error',
        content: `Password reset failed.  Please try again.`,
      });
      console.error(error);
    }
  }),
  checkPartnerUpdate: thunkOn(
    (actions, storeActions) => [actions.authenticationSuccess],
    (actions, payload, { getStoreState }) => {
      const url = extractPartnerSlug(window.location.pathname);
      const { session: { user: { partners = [], sessionPartnerId = '' } = {} } = {} } = getStoreState();
      const newPartner = partners.find((p: PartnerLink) => p.urlSlugName === url);
      if (newPartner && newPartner.partnerId !== sessionPartnerId) {
        actions.setActivePartner({ partnerId: newPartner.partnerId, discardPath: false });
      }
    },
  ),
  checkSessionPartner: thunkOn(
    (actions, storeActions) => [
      storeActions.navigation.setActiveNavigation,
      storeActions.workflow.WorkflowActionStart,
      storeActions.shipments.shipmentActionStart,
    ],
    (actions, payload, { getStoreState }) => {
      actions.verifyPartner();
    },
  ),
  refreshSession: thunk(async (actions, payload, { getStoreState, injections }) => {
    try {
      actions.sessionActionStart();
      const { authService } = injections;
      // const refreshToken = cookies.get(REFRESH_TOKEN_NAME);
      actions.verifyPartner();
      if (getLoginCookie()) {
        actions.setRefreshing(true);
        const response = await authService.refresh();
        actions.setRefreshing(false);
        actions.authenticationSuccess(response);
        return response;
      }
    } catch (e) {
      actions.setRefreshing(false);
      actions.authenticationFailure({
        error: e,
        message: 'Session Expired',
        asNotification: false,
      });
      actions.logoutSso();
    }
  }),
  refreshSessionSilent: thunk(async (actions, payload, { getStoreState, injections }) => {
    try {
      const { authService } = injections;
      actions.verifyPartner();
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
      const response = await authService.refreshPoll();
      actions.authenticationSuccessSilent(response);
      return response;
    } catch (e) {
      // Let the caller (SessionProvider) handle the failure
      // by returning undefined, which triggers returnToLogin flow
      return undefined;
    }
  }),
  verifyPartner: action((state, payload) => {
    const sessionPartnerId = state.user?.sessionPartnerId;
    const cookiePartnerId = getPartnerId();
    // if our partner cookie does not match our session cookie in redux, force the user to login.
    if (sessionPartnerId && cookiePartnerId && sessionPartnerId !== cookiePartnerId) {
      state.isRefreshing = true;
      window.location.href = '/logout';
    }
  }),
  logoutSso: thunk(async (actions, payload, { injections }) => {
    const { authService } = injections;
    // authService.logout() redirects via window.location.href
    // No code after this will meaningfully execute as the page navigates away
    await authService.logout();
  }),
  authenticationSuccess: action((state, payload) => {
    payload.user.partners = payload.user.partners?.filter((partner) => partner.urlSlugName);
    state.user = payload.user;
    state.accessToken = payload.accessToken;
    state.refreshToken = payload.refreshToken;
  }),
  authenticationSuccessSilent: action((state, payload) => {
    payload.user.partners = payload.user.partners?.filter((partner) => partner.urlSlugName);
    state.user = payload.user;
    state.accessToken = payload.accessToken;
    state.refreshToken = payload.refreshToken;
  }),
  authenticationFailure: action((state, payload) => {
    delete state.user;
    delete state.accessToken;
    delete state.refreshToken;
    cookies.remove(REFRESH_TOKEN_NAME, { path: '/' });
    cookies.remove(ACCESS_TOKEN_NAME, { path: '/' });
  }),
  fetchApplicationVersion: thunk(async (actions, payload, { injections }) => {
    try {
      const data = await injections.authService.getVersionMetadata();
      actions.setVersionMetadata(data);
      return data;
    } catch (error) {
      return error;
    }
  }),
  setChangePasswordErrorMessage: action((state, message) => {
    state.changePasswordErrorMessage = message;
  }),
  setSessionUser: action((state, user) => {
    state.user = user;
  }),
  setVersionMetadata: action((state, metadata) => {
    state.versionMetadata = metadata;
  }),
  isLoggedIn: computed((state) => {
    const cookiesSet = getLoginCookie();
    return state.user != null && cookiesSet;
  }),
  canEdit: computed((state) => {
    const role = state.user?.sessionPartnerRole;
    return role ? role === 'administrator' || role === 'editor' : false;
  }),
  isAdmin: computed((state) => {
    const role = state.user?.sessionPartnerRole;
    return role ? role === 'administrator' : false;
  }),
  setRefreshing: action((state, payload) => {
    state.isRefreshing = payload;
  }),
};
